import { closeLiveSearchSubscription, closeLiveSearchSubscriptions } from './api/loads.api'
import { log } from 'utils/logger'

/**
 * Release a live-search subscription, best-effort.
 *
 * Every caller wants the same three things — fire it, never await it, never let a failure propagate — and
 * every caller had written them out again: three times in `useLiveLoadSearch` and once more in
 * `useBoxTruckLiveSearch`. A failed close is not actionable at the call site (the subscription expires on its
 * own TTL regardless), so the only thing that differs between them is the `context` for the log line.
 */
export const closeLiveSearchQuietly = (subscriptionId: string, context: Record<string, unknown>): void => {
    void closeLiveSearchSubscription(subscriptionId).catch((error: unknown) => {
        log.e('[live-search] failed to close subscription', error instanceof Error ? error : new Error(String(error)), { subscriptionId, ...context })
    })
}

/**
 * The same for a set released together, in ONE request.
 *
 * Not only cheaper. A board fans one search out into a subscription per pinned driver and releases them
 * together — a narrowed re-search, a closed tab, a page teardown — and on teardown a browser unloading the
 * page drops in-flight requests. One request arrives far more often than ten, and what the dropped ones cost
 * is subscriptions left live to their TTL, matching every ingested posting against a search nobody is
 * looking at.
 */
export const closeLiveSearchesQuietly = (subscriptionIds: string[], context: Record<string, unknown>): void => {
    if (subscriptionIds.length === 0) return
    void closeLiveSearchSubscriptions(subscriptionIds).catch((error: unknown) => {
        log.e('[live-search] failed to close subscriptions', error instanceof Error ? error : new Error(String(error)), {
            count: subscriptionIds.length,
            ...context,
        })
    })
}
